"use client";

import type { JSX } from "react";
import { Icon, Link } from "@/components";
import { useLayout } from "@/providers";
import { moduleLabel } from "@/utils/elearning/moduleLabel";

export interface ModuleNextCtaProps {
  /** Inscripción activa (arma los destinos de navegación). */
  enrollmentId: string;
  /** Módulo siguiente en la secuencia (null si el actual es el último). */
  nextModule: {
    moduleId: string;
    order: number;
    displayName: string | null;
  } | null;
  /** El módulo actual es el último del curso. */
  esUltimo: boolean;
  /** El certificado ya está disponible (curso aprobado y el curso lo emite). */
  certificadoDisponible: boolean;
  /** true mientras el progreso no llegó: no se muestra nada (evita CTAs erróneos). */
  loading?: boolean;
}

/**
 * @name ModuleNextCta
 * @description CTA de avance al completar un módulo: continuar al módulo siguiente
 * (que el servidor ya habrá desbloqueado) o, si era el último, ver el certificado
 * recién emitido. Evita obligar al alumno a volver a la lista de módulos para
 * seguir la cursada. Si el curso no quedó aprobado aún (o no emite certificado),
 * degrada a "volver al curso" como cierre neutro.
 *
 * @param {ModuleNextCtaProps} props - Navegación derivada del progreso del servidor.
 * @returns {JSX.Element | null} El CTA de avance (o null mientras carga).
 */
const ModuleNextCta = ({
  enrollmentId,
  nextModule,
  esUltimo,
  certificadoDisponible,
  loading = false
}: ModuleNextCtaProps): JSX.Element | null => {
  const { t } = useLayout();

  if (loading) {
    return null;
  }

  if (nextModule) {
    return (
      <Link
        href={`/elearning/mi-aprendizaje/cursos/${enrollmentId}/modulos/${nextModule.moduleId}`}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
      >
        {`${t("continueTo")} ${moduleLabel({ order: nextModule.order, label: nextModule.displayName }, t)}`}
        <Icon name="fa-solid fa-arrow-right" className="text-xs!" />
      </Link>
    );
  }

  if (esUltimo && certificadoDisponible) {
    return (
      <Link
        href={`/elearning/mi-aprendizaje/cursos/${enrollmentId}/certificado`}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
      >
        {t("viewMyCertificate")}
        <Icon name="fa-solid fa-arrow-right" className="text-xs!" />
      </Link>
    );
  }

  return (
    <Link
      href={`/elearning/mi-aprendizaje/cursos/${enrollmentId}`}
      className="inline-flex items-center gap-2 min-h-11 sm:min-h-0 text-sm font-medium text-primary hover:underline"
    >
      {t("backToCourse")}
      <Icon name="fa-solid fa-arrow-right" className="text-xs!" />
    </Link>
  );
};

export default ModuleNextCta;
